/**
 * Who the demo is for.
 *
 * One entry per marketing industry page, keyed by that page's slug, so
 * /industries/<slug> and /demo/<slug> stay one pair and a new industry is a
 * single data entry on each side. `demo/profiles.test.ts` fails the build if a
 * marketing slug arrives here with nothing to open.
 *
 * What varies between entries is what a client would actually be asked for:
 * the archetype, the open list, the document kinds, one delivered file, and
 * one message thread. The shell, the nav, and every behavior are the product
 * and do not vary. If two industries end up with the same list, one of them is
 * not a persona.
 *
 * Client names, amounts, and file names are sample data, written to look like
 * a real month rather than a demo. They match the names the matching marketing
 * page already prints, so a prospect who clicks through from that page sees the
 * clients it just showed them.
 */

import {
  demoIndustries,
  portalSampleClient,
  type DemoIndustrySlug,
} from "@ledgerbox/domain"

/** A thing the firm asked for, in the client's list. */
export type DemoItem =
  | {
      kind: "document"
      name: string
      description?: string
      /** Left out means required. */
      optional?: true
      /** Set when the client already sent it. */
      sent?: { fileName: string; kb: number; daysAgo: number }
      /** Set when the firm has already read and accepted it. */
      accepted?: true
    }
  | { kind: "text" | "date"; name: string; description?: string; answer?: string }
  | { kind: "choice"; name: string; description?: string; options: string[]; answer?: string }
  | { kind: "yes_no"; name: string; description?: string; answer?: boolean }

export type DemoProfile = {
  /** Matches the marketing page slug. */
  slug: DemoIndustrySlug
  /** What is already in the demo, as the gate's sentence says it. */
  collects: string
  /** The firm's name when the prospect gave no website to read. */
  fallbackFirmName: string
  /** The accent used with that fallback name. */
  fallbackAccent: string
  /** Who the firm sends the link to. */
  client: { company: string; contactName: string; contactEmail: string }
  /** The person at the firm whose name is on the messages. */
  firmContact: string
  /** What the request is called. `adhoc` names itself; a period is its month. */
  request:
    | { kind: "period"; monthsAgo: number; dueInDays: number }
    | { kind: "adhoc"; title: string; dueInDays: number }
  items: DemoItem[]
  /** What the firm sent back. The file a client opens on For you. */
  delivered: { fileName: string; daysAgo: number; heading: string; lines: string[] }
  /** The one thread, oldest message first. */
  thread: {
    subject: string
    messages: { side: "firm" | "client"; body: string; hoursAgo: number }[]
  }
  /** Months the client has already closed out, newest first. */
  closedMonths: { monthsAgo: number; fileCount: number }[]
}

const closedYear = [
  { monthsAgo: 1, fileCount: 6 },
  { monthsAgo: 2, fileCount: 5 },
  { monthsAgo: 3, fileCount: 7 },
]

export const demoProfiles = {
  /* The /firms landing page's default, and the one the in-app preview uses.
     A bookkeeper reading their own portal should see their own trade. */
  firms: {
    slug: "firms",
    collects: "a month of a plumbing client's statements, card charges and receipts",
    fallbackFirmName: "Your firm",
    fallbackAccent: "#2f6f4e",
    // The studio's address bar and email previews name this client too.
    client: {
      company: portalSampleClient.company,
      contactName: portalSampleClient.contactName,
      contactEmail: portalSampleClient.contactEmail,
    },
    firmContact: "Ruth",
    request: { kind: "period", monthsAgo: 0, dueInDays: 6 },
    items: [
      {
        kind: "document",
        name: "Operating account statement",
        description: "The full month, all pages. A PDF from the bank reads better than a screenshot.",
        sent: { fileName: "operating_statement.pdf", kb: 412, daysAgo: 2 },
      },
      {
        kind: "document",
        name: "Business card statement",
        description: "The card you buy materials on.",
      },
      {
        kind: "document",
        name: "Receipts over $75",
        description: "Photos are fine. Send them as you go; they do not have to come at once.",
        optional: true,
      },
      {
        kind: "yes_no",
        name: "Did you take money out of the business this month?",
        description: "Owner draws are not expenses, so they are posted differently.",
      },
      {
        kind: "text",
        name: "Anything unusual this month?",
        description: "A large purchase, a refund, a new account. One line is enough.",
        answer: "Bought a second van on the 14th. Financed through the credit union.",
      },
    ],
    delivered: {
      fileName: "hartley-plumbing-march.xlsx",
      daysAgo: 1,
      heading: "March transactions",
      lines: ["214 rows", "2 rows flagged for a reason", "Balances tie to the statement"],
    },
    thread: {
      subject: "The van purchase",
      messages: [
        {
          side: "firm",
          body: "I see a $28,400 payment on the 14th. Is that the van, and is the loan in the business name?",
          hoursAgo: 30,
        },
        {
          side: "client",
          body: "Yes, the van. The loan is in the business name. I can send the paperwork if you need it.",
          hoursAgo: 26,
        },
        {
          side: "firm",
          body: "Please do. I will set it up as an asset with the loan against it rather than an expense.",
          hoursAgo: 25,
        },
      ],
    },
    closedMonths: closedYear,
  },

  accountants: {
    slug: "accountants",
    collects: "a month of a cafe's bank, merchant and supplier documents",
    fallbackFirmName: "Your firm",
    fallbackAccent: "#2f6f4e",
    client: {
      company: "Marisol Cafe",
      contactName: "Marisol Vega",
      contactEmail: "marisol@marisolcafe.com",
    },
    firmContact: "Ruth",
    request: { kind: "period", monthsAgo: 0, dueInDays: 5 },
    items: [
      {
        kind: "document",
        name: "Bank statement",
        description: "The full month, all pages.",
        sent: { fileName: "statement_mar.pdf", kb: 388, daysAgo: 3 },
        accepted: true,
      },
      {
        kind: "document",
        name: "Merchant processor statement",
        description: "The card processor's monthly statement, with the fees on it.",
      },
      {
        kind: "document",
        name: "Supplier invoices",
        description: "Everything you were billed this month.",
      },
      {
        kind: "choice",
        name: "How do you want the payroll journal posted?",
        options: ["By pay run", "One entry for the month"],
        description: "Either works. By pay run is easier to check against the bank.",
      },
      {
        kind: "yes_no",
        name: "Are there any tips paid out in cash?",
        description: "Cash tips are reported differently from tips on a card.",
        answer: true,
      },
    ],
    delivered: {
      fileName: "marisol-cafe-february.xlsx",
      daysAgo: 4,
      heading: "February transactions",
      lines: ["341 rows", "Merchant fees split out", "Balances tie to the statement"],
    },
    thread: {
      subject: "February merchant fees",
      messages: [
        {
          side: "firm",
          body: "February's processor fees came to $1,180, up about $300 on January. Higher card volume, nothing odd in it.",
          hoursAgo: 96,
        },
        {
          side: "client",
          body: "That tracks. We ran the Valentine's menu and almost nobody paid cash.",
          hoursAgo: 92,
        },
      ],
    },
    closedMonths: closedYear,
  },

  "tax-preparers": {
    slug: "tax-preparers",
    collects: "a consulting client's year-end statements, 1099s and open questions",
    fallbackFirmName: "Your practice",
    fallbackAccent: "#2f6f4e",
    client: {
      company: "Alvarez Consulting",
      contactName: "Luis Alvarez",
      contactEmail: "luis@alvarezconsulting.com",
    },
    firmContact: "Priya",
    request: { kind: "adhoc", title: "2025 return", dueInDays: 9 },
    items: [
      {
        kind: "document",
        name: "Year-end bank statements",
        description: "December for each business account, or the full year if you have it.",
        sent: { fileName: "statement_q4.pdf", kb: 524, daysAgo: 5 },
      },
      {
        kind: "document",
        name: "1099s you received",
        description: "Every one, including the ones you think are duplicates.",
      },
      {
        kind: "document",
        name: "Vehicle mileage log",
        optional: true,
        description: "Only if you are claiming the business use of a car.",
      },
      {
        kind: "date",
        name: "When did the home office start being used?",
        description: "The month is enough.",
      },
      {
        kind: "yes_no",
        name: "Did you pay anyone as a contractor this year?",
        description: "$600 or more to one person means a 1099 goes out.",
      },
    ],
    delivered: {
      fileName: "alvarez-2024-return.pdf",
      daysAgo: 300,
      heading: "2024 return",
      lines: ["Filed and accepted", "Copy for your records", "Estimates for the year attached"],
    },
    thread: {
      subject: "The contractor payments",
      messages: [
        {
          side: "firm",
          body: "Three people crossed $600 this year. I need their W-9s before the 1099s can go out.",
          hoursAgo: 48,
        },
        {
          side: "client",
          body: "I have two of them. The third stopped answering. What happens then?",
          hoursAgo: 40,
        },
        {
          side: "firm",
          body: "We file with what we have and note the attempt. Send the two and I will start there.",
          hoursAgo: 38,
        },
      ],
    },
    closedMonths: [
      { monthsAgo: 12, fileCount: 14 },
      { monthsAgo: 24, fileCount: 11 },
    ],
  },

  lenders: {
    slug: "lenders",
    collects: "an applicant's four months of statements and their ID",
    fallbackFirmName: "Your credit team",
    fallbackAccent: "#2f6f4e",
    client: {
      company: "Beacon Auto Repair",
      contactName: "Tom Beacon",
      contactEmail: "tom@beaconautorepair.com",
    },
    firmContact: "Alex",
    request: { kind: "adhoc", title: "Working capital application", dueInDays: 2 },
    items: [
      {
        kind: "document",
        name: "Last four bank statements",
        description: "Every page of each month, straight from the bank. Send them as PDFs, not screenshots.",
        sent: { fileName: "statement_jun.pdf", kb: 468, daysAgo: 1 },
      },
      {
        kind: "document",
        name: "Statements for any second account",
        description: "If revenue lands anywhere other than the account above.",
        optional: true,
      },
      {
        kind: "document",
        name: "Photo ID",
        description: "A driver's licence or a passport.",
      },
      {
        kind: "text",
        name: "How much are you asking for?",
        description: "A number is fine. We will talk about the term on the call.",
        answer: "$60,000",
      },
      {
        kind: "choice",
        name: "What is the money for?",
        options: ["Equipment", "Inventory", "Payroll", "Refinancing", "Something else"],
      },
      {
        kind: "yes_no",
        name: "Do you have finance in place with anyone else right now?",
        description: "Including a merchant cash advance.",
      },
    ],
    delivered: {
      fileName: "beacon-auto-terms.pdf",
      daysAgo: 0,
      heading: "Indicative terms",
      lines: ["Subject to the statements above", "Good for 14 days", "No charge to decline"],
    },
    thread: {
      subject: "The second account",
      messages: [
        {
          side: "firm",
          body: "Most of the deposits in June come from one source. Is there another account the card sales land in?",
          hoursAgo: 20,
        },
        {
          side: "client",
          body: "Yes, the card money goes to a separate account at a different bank. I did not think you needed it.",
          hoursAgo: 14,
        },
        {
          side: "firm",
          body: "We do, and it will help you. It is the account that shows the real revenue.",
          hoursAgo: 13,
        },
      ],
    },
    closedMonths: [],
  },

  "mortgage-lenders": {
    slug: "mortgage-lenders",
    collects: "a borrower's statements, pay stubs and the underwriter's conditions",
    fallbackFirmName: "Your brokerage",
    fallbackAccent: "#2f6f4e",
    client: {
      company: "Loan 4417",
      contactName: "Mai Nguyen",
      contactEmail: "mai.nguyen@example.com",
    },
    firmContact: "Devon",
    request: { kind: "adhoc", title: "Loan 4417 · conditions", dueInDays: 3 },
    items: [
      {
        kind: "document",
        name: "Two months of bank statements",
        description: "Every page of each month, including the blank ones. The underwriter counts pages.",
        sent: { fileName: "statement_jun.pdf", kb: 502, daysAgo: 2 },
      },
      {
        kind: "document",
        name: "Statements for the savings account",
        description: "The account the deposit is coming from.",
      },
      {
        kind: "document",
        name: "Last two pay stubs",
      },
      {
        kind: "text",
        name: "Where did the $12,000 deposit on the 3rd come from?",
        description: "Any deposit that is not payroll needs a sentence and a paper trail.",
      },
      {
        kind: "yes_no",
        name: "Is any part of the deposit a gift?",
        description: "A gift needs a letter from the person who gave it.",
      },
    ],
    delivered: {
      fileName: "loan-4417-conditions.pdf",
      daysAgo: 1,
      heading: "Outstanding conditions",
      lines: ["Three items left", "Underwriter's wording for each", "Nothing else is needed from you"],
    },
    thread: {
      subject: "The deposit on the 3rd",
      messages: [
        {
          side: "firm",
          body: "Underwriting flagged the $12,000 on June 3rd. They need where it came from and a statement from that account.",
          hoursAgo: 36,
        },
        {
          side: "client",
          body: "It is from my brother, toward the down payment. Does that cause a problem?",
          hoursAgo: 30,
        },
        {
          side: "firm",
          body: "No, but it makes it a gift. I will send the letter for him to sign and it goes in here.",
          hoursAgo: 29,
        },
      ],
    },
    closedMonths: [],
  },

  "family-law-forensic": {
    slug: "family-law-forensic",
    collects: "two years of an opposing party's statements, and what is still missing",
    fallbackFirmName: "Your practice",
    fallbackAccent: "#2f6f4e",
    client: {
      company: "Kessler v. Kessler",
      contactName: "Ellen Kessler",
      contactEmail: "ellen.kessler@example.com",
    },
    firmContact: "Marc",
    request: { kind: "adhoc", title: "Discovery · financial records", dueInDays: 12 },
    items: [
      {
        kind: "document",
        name: "Personal bank statements, 24 months",
        description: "Every account in your name. Whole statements, not printouts of a search.",
        sent: { fileName: "statement_2024.pdf", kb: 1840, daysAgo: 6 },
      },
      {
        kind: "document",
        name: "Credit card statements, 24 months",
      },
      {
        kind: "document",
        name: "Brokerage and retirement statements",
        description: "Year-end for each of the last two years.",
      },
      {
        kind: "document",
        name: "Statements for any account held with someone else",
        description: "Joint accounts, a parent's account you are on, a business account.",
        optional: true,
      },
      {
        kind: "text",
        name: "Are there accounts you can no longer access?",
        description: "Name them here. We can ask for them another way.",
      },
    ],
    delivered: {
      fileName: "kessler-schedule-of-accounts.pdf",
      daysAgo: 3,
      heading: "Schedule of accounts",
      lines: ["Every account found so far", "What is still outstanding", "Read this before the deposition"],
    },
    thread: {
      subject: "The closed account",
      messages: [
        {
          side: "firm",
          body: "The 2023 statements show transfers to an account that stops appearing in June. Do you know what it was?",
          hoursAgo: 72,
        },
        {
          side: "client",
          body: "That was the joint savings. He closed it without telling me. I do not have any of those statements.",
          hoursAgo: 64,
        },
        {
          side: "firm",
          body: "Understood. Put it in the answer above and we will request it from the bank directly.",
          hoursAgo: 62,
        },
      ],
    },
    closedMonths: [],
  },

  "property-managers": {
    slug: "property-managers",
    collects: "a building's operating and trust statements and its repair invoices",
    fallbackFirmName: "Your management company",
    fallbackAccent: "#2f6f4e",
    client: {
      company: "Larkspur Court",
      contactName: "Ben Okoro",
      contactEmail: "ben@larkspurcourt.example.com",
    },
    firmContact: "Sam",
    request: { kind: "period", monthsAgo: 0, dueInDays: 4 },
    items: [
      {
        kind: "document",
        name: "Operating account statement",
        description: "The account rent is deposited into.",
        sent: { fileName: "statement_mar.pdf", kb: 296, daysAgo: 2 },
        accepted: true,
      },
      {
        kind: "document",
        name: "Trust account statement",
        description: "Security deposits are held separately, so the statement comes separately.",
      },
      {
        kind: "document",
        name: "Contractor invoices",
        description: "Repairs and maintenance billed this month.",
      },
      {
        kind: "choice",
        name: "Which unit was the plumbing work on?",
        options: ["Unit 2A", "Unit 3B", "Common area"],
        description: "It changes which owner is charged for it.",
      },
      {
        kind: "yes_no",
        name: "Did any deposit get returned this month?",
        description: "A returned deposit leaves the trust account, not the operating one.",
      },
    ],
    delivered: {
      fileName: "larkspur-court-february.pdf",
      daysAgo: 2,
      heading: "February owner statement",
      lines: ["Rent collected and held", "Repairs by unit", "What is paid out on the 5th"],
    },
    thread: {
      subject: "Unit 3B plumbing",
      messages: [
        {
          side: "firm",
          body: "The plumber's invoice says \"upstairs bath\". I need the unit before I can charge it to an owner.",
          hoursAgo: 26,
        },
        {
          side: "client",
          body: "3B. The tenant reported it on the 9th.",
          hoursAgo: 22,
        },
      ],
    },
    closedMonths: closedYear,
  },

  "ecommerce-sellers": {
    slug: "ecommerce-sellers",
    collects: "a seller's marketplace payouts, processor statement and bank",
    fallbackFirmName: "Your bookkeeper",
    fallbackAccent: "#2f6f4e",
    client: {
      company: "Northbank Goods",
      contactName: "Ivy Chen",
      contactEmail: "ivy@northbankgoods.com",
    },
    firmContact: "Ruth",
    request: { kind: "period", monthsAgo: 0, dueInDays: 5 },
    items: [
      {
        kind: "document",
        name: "Marketplace payout statements",
        description: "One for each place you sell. The payout report, not the sales summary.",
        sent: { fileName: "payout_mar.pdf", kb: 244, daysAgo: 3 },
      },
      {
        kind: "document",
        name: "Card processor statement",
        description: "For sales off your own site.",
      },
      {
        kind: "document",
        name: "Bank statement",
        description: "So the payouts can be matched to what landed.",
      },
      {
        kind: "document",
        name: "Advertising invoices",
        optional: true,
        description: "Only if you were billed separately rather than having it taken out of a payout.",
      },
      {
        kind: "text",
        name: "Which marketplaces did you sell on this month?",
        description: "Name each one, including a new one you only tried.",
        answer: "Amazon, Shopify, and a first month on Etsy.",
      },
    ],
    delivered: {
      fileName: "northbank-goods-february.xlsx",
      daysAgo: 5,
      heading: "February payouts",
      lines: ["Every payout broken into sales, fees, refunds", "Reconciled to the bank", "One row per transaction"],
    },
    thread: {
      subject: "The Etsy payout",
      messages: [
        {
          side: "firm",
          body: "There is a payout from Etsy I did not have a statement for. Is that a new channel this month?",
          hoursAgo: 60,
        },
        {
          side: "client",
          body: "Yes, we opened it in March. I will add the statement here.",
          hoursAgo: 52,
        },
      ],
    },
    closedMonths: closedYear,
  },

  nonprofits: {
    slug: "nonprofits",
    collects: "a grant quarter's statements, receipts and budget-line questions",
    fallbackFirmName: "Your finance team",
    fallbackAccent: "#2f6f4e",
    client: {
      company: "Harbor Youth Fund",
      contactName: "Nina Osei",
      contactEmail: "nina@harboryouthfund.org",
    },
    firmContact: "Ruth",
    request: { kind: "adhoc", title: "Ridge Trail grant · Q1 report", dueInDays: 7 },
    items: [
      {
        kind: "document",
        name: "Bank statements for the quarter",
        description: "All three months of the account the grant was paid into.",
        sent: { fileName: "statement_q1.pdf", kb: 356, daysAgo: 4 },
      },
      {
        kind: "document",
        name: "Receipts for grant spending",
        description: "Anything paid for with grant money. Photos are fine.",
      },
      {
        kind: "document",
        name: "Payroll reports",
        description: "For staff time charged to the grant.",
      },
      {
        kind: "choice",
        name: "Which budget line covers the van rental?",
        options: ["Program delivery", "Transport", "Equipment"],
        description: "The funder reports against the line, so it has to be the one in the award letter.",
      },
      {
        kind: "yes_no",
        name: "Was any grant money spent outside the award period?",
        description: "Spending before or after the dates has to be reported separately.",
      },
    ],
    delivered: {
      fileName: "ridge-trail-q1-report.xlsx",
      daysAgo: 2,
      heading: "Q1 grant report",
      lines: ["Spending against each budget line", "What is left in the award", "Backing receipts listed per line"],
    },
    thread: {
      subject: "The van rental",
      messages: [
        {
          side: "firm",
          body: "The van rental is $2,100 of the quarter. The award letter has transport and program delivery as separate lines.",
          hoursAgo: 44,
        },
        {
          side: "client",
          body: "It was for the summer trips, so program delivery. Does transport only cover the minibus lease?",
          hoursAgo: 38,
        },
        {
          side: "firm",
          body: "That is how the funder reads it. I will put it under program delivery and note why.",
          hoursAgo: 36,
        },
      ],
    },
    closedMonths: [{ monthsAgo: 3, fileCount: 9 }, { monthsAgo: 6, fileCount: 8 }],
  },

  "real-estate-investors": {
    slug: "real-estate-investors",
    collects: "a month across four properties, with the mortgage statements",
    fallbackFirmName: "Your bookkeeper",
    fallbackAccent: "#2f6f4e",
    client: {
      company: "Ridgeway Holdings",
      contactName: "Grant Ridgeway",
      contactEmail: "grant@ridgewayholdings.example.com",
    },
    firmContact: "Sam",
    request: { kind: "period", monthsAgo: 0, dueInDays: 6 },
    items: [
      {
        kind: "document",
        name: "Statements for each property account",
        description: "One statement per LLC. Send them all, even the quiet ones.",
        sent: { fileName: "statements_mar.pdf", kb: 704, daysAgo: 2 },
      },
      {
        kind: "document",
        name: "Mortgage statements",
        description: "So interest and principal can be split.",
      },
      {
        kind: "document",
        name: "Repair and improvement invoices",
        description: "Both kinds. Which is which is a decision, and it is ours to make.",
      },
      {
        kind: "text",
        name: "Which property is the $9,400 roof work on?",
        description: "It is a capital item, so it attaches to one property for years.",
      },
      {
        kind: "yes_no",
        name: "Did you buy or sell anything this month?",
        description: "A closing statement changes the whole month.",
      },
    ],
    delivered: {
      fileName: "ridgeway-february-by-property.xlsx",
      daysAgo: 4,
      heading: "February by property",
      lines: ["One tab per property", "Interest split from principal", "Repairs kept apart from improvements"],
    },
    thread: {
      subject: "The roof work",
      messages: [
        {
          side: "firm",
          body: "The $9,400 roof invoice does not name a property. It is an improvement, so it sits with one building for years.",
          hoursAgo: 52,
        },
        {
          side: "client",
          body: "Old Mill. The whole rear section was replaced.",
          hoursAgo: 46,
        },
        {
          side: "firm",
          body: "Good. A full replacement is an improvement rather than a repair, so it will not all land in this year.",
          hoursAgo: 45,
        },
      ],
    },
    closedMonths: closedYear,
  },
} as const satisfies Record<DemoIndustrySlug, DemoProfile>

/** The persona the in-app preview and a bare /demo open on. */
export const defaultProfileSlug: DemoIndustrySlug = "firms"

export const demoProfileList: DemoProfile[] = Object.values(demoProfiles)

/** What the persona is called, from the one list all three surfaces share. */
export const demoProfileLabel = (profile: DemoProfile): string =>
  demoIndustries[profile.slug]

/** An unknown slug opens the default rather than a dead end: a demo link that
 * outlives the industry page it was written for should still open. */
export const findDemoProfile = (slug: string | null | undefined): DemoProfile =>
  (slug && slug in demoProfiles
    ? demoProfiles[slug as DemoIndustrySlug]
    : demoProfiles[defaultProfileSlug]) as DemoProfile
